/**
 * The drag band of a frame window (`frame-window-shell.tsx`): the top of its
 * paper moves the window, like a title bar, wherever the press lands on
 * nothing the content handles itself. Controls, links, fields, editor text,
 * scrollbars, anything showing a pointer or text cursor and any text (read
 * only too, so it stays selectable) keep their own press; the empty space
 * around them, and an editor's top padding above its first block, is band.
 * Iframes swallow pointer events, so the shell lays a shield over the part
 * of an iframe inside the band.
 */
/** Height of the band from the top of the window's paper, in px. */
export const FRAME_WINDOW_DRAG_BAND = 32;

/** Elements whose own press the band never takes. */
const INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  "input",
  "textarea",
  "select",
  "option",
  "label",
  "summary",
  "video",
  "audio",
  "iframe",
  "[role='button']",
  "[role='link']",
  "[role='tab']",
  "[role='menuitem']",
  "[role='option']",
  "[role='checkbox']",
  "[role='radio']",
  "[role='switch']",
  "[role='slider']",
  "[role='combobox']",
  "[draggable='true']",
  "[data-no-drag]",
].join(", ");

/** Cursors that promise nothing to press: anything else (pointer, text,
 *  resize, ...) marks content with a press of its own. */
const PASSIVE_CURSORS = new Set(["", "auto", "default", "grab", "grabbing"]);

/** A press point in client pixels. */
export interface BandPoint {
  clientX: number;
  clientY: number;
}

/** The nearest `selector` match from `target` up to `root`, inclusive. */
function closestWithin(target: Element, selector: string, root: Element) {
  const match = target.closest(selector);
  return match && root.contains(match) ? match : null;
}

/** Whether `point` lies in the top padding of editing host `host`. */
function inTopPadding(host: Element, point: BandPoint): boolean {
  const contentTop =
    host.getBoundingClientRect().top +
    host.clientTop +
    (parseFloat(getComputedStyle(host).paddingTop) || 0);
  return point.clientY < contentTop;
}

/** Whether `point` lies on a line of one of `element`'s own text nodes:
 *  the deepest element under a point owns the text there. */
function onOwnText(element: Element, point: BandPoint): boolean {
  const range = document.createRange();
  for (const node of element.childNodes) {
    if (node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim()) continue;
    range.selectNodeContents(node);
    for (const line of range.getClientRects()) {
      if (
        point.clientX >= line.left &&
        point.clientX < line.right &&
        point.clientY >= line.top &&
        point.clientY < line.bottom
      )
        return true;
    }
  }
  return false;
}

/** Whether `point` lies on `element`'s vertical scrollbar. */
function onScrollbar(element: Element, point: BandPoint): boolean {
  if (element.clientWidth <= 0 || element.scrollHeight <= element.clientHeight)
    return false;
  const left = element.getBoundingClientRect().left + element.clientLeft;
  return point.clientX - left >= element.clientWidth;
}

/** Whether the content of window `root` handles a press on `target` (an
 *  element inside it) at `point` itself: a control, editable or selectable
 *  text, a scrollbar, anything with a cursor that promises a press. */
export function keepsOwnPress(
  target: Element,
  point: BandPoint,
  root: Element,
): boolean {
  if (closestWithin(target, INTERACTIVE_SELECTOR, root)) return true;
  // Editable text keeps its press; a `false` island (a widget) does not.
  const editable = closestWithin(target, "[contenteditable]", root);
  if (editable && editable.getAttribute("contenteditable") !== "false")
    return !(editable === target && inTopPadding(editable, point));
  if (!PASSIVE_CURSORS.has(getComputedStyle(target).cursor)) return true;
  return onOwnText(target, point) || onScrollbar(target, point);
}

/** Whether a press on `target` at `point` starts a drag of window `root`
 *  from its band. */
export function isFrameWindowBandPress(
  target: EventTarget | null,
  point: BandPoint,
  root: Element | null,
): boolean {
  if (!root || !(target instanceof Element) || !root.contains(target))
    return false;
  const depth = point.clientY - root.getBoundingClientRect().top;
  if (depth < 0 || depth >= FRAME_WINDOW_DRAG_BAND) return false;
  return !keepsOwnPress(target, point, root);
}

/** A shield over the band part of an iframe, in the window's pixels. */
export interface FrameWindowBandShield {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Shields over every iframe of window `root` that reaches into its band. */
export function frameWindowBandShields(root: Element): FrameWindowBandShield[] {
  const rootRect = root.getBoundingClientRect();
  const bandBottom = rootRect.top + FRAME_WINDOW_DRAG_BAND;
  const shields: FrameWindowBandShield[] = [];
  for (const iframe of root.querySelectorAll("iframe")) {
    const rect = iframe.getBoundingClientRect();
    const top = Math.max(rect.top, rootRect.top);
    const bottom = Math.min(rect.bottom, bandBottom);
    if (bottom <= top || rect.width <= 0) continue;
    shields.push({
      left: rect.left - rootRect.left,
      top: top - rootRect.top,
      width: rect.width,
      height: bottom - top,
    });
  }
  return shields;
}

export function sameFrameWindowBandShields(
  a: readonly FrameWindowBandShield[],
  b: readonly FrameWindowBandShield[],
): boolean {
  return (
    a.length === b.length &&
    a.every(
      (shield, index) =>
        shield.left === b[index]!.left &&
        shield.top === b[index]!.top &&
        shield.width === b[index]!.width &&
        shield.height === b[index]!.height,
    )
  );
}
